<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-container fluid class="px-6 py-6 full-height-container">
      <v-row class="fill-height">
        <!-- LEFT SIDE: SCHEDULE SECTION -->
        <v-col cols="12" md="4" lg="3" class="d-flex">
          <v-card outlined class="pa-6">
            <div class="text-h5 font-weight-bold mb-2">Schedule Batch Resync</div>
            <div class="text-body-2 text--secondary mb-6">
              Select a TPR Date, Batch Type, and schedule the day to run the resync
            </div>
            <v-form ref="sync-form">
              <!-- Batch Type Drow Down -->
              <v-autocomplete
                v-model="syncBatchType"
                :items="batchTypes"
                outlined
                dense
                background-color="input"
                label="Batch Type"
                class="mb-4"
              ></v-autocomplete>

              <!-- TPR Date Picker -->
              <v-menu
                bottom
                min-width="auto"
                offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                  :rules="[v => !!v || 'Date Required']"
                  :value="dateText(syncTPRDate)"
                  background-color="input"
                  class="mb-4"
                  clearable
                  readonly
                  dense
                  label="TPR Date to Sync"
                  outlined
                  v-on="on"
                  v-bind="attrs"
                  @click:clear="syncTPRDate = null">
                    <template #append-outer>
                      <v-tooltip right color="black">
                        <template #activator="{ on: tooltip, attrs: tooltipAttrs }">
                          <v-icon
                            v-bind="tooltipAttrs"
                            v-on="tooltip">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>Only TPR batches greater than 2 weeks away can be scheduled for Resync</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  :allowed-dates="allowedTPRDates"
                  :show-current="true"
                  color="primary"
                  no-title
                  v-model="syncTPRDate">
                </v-date-picker>
              </v-menu>

              <!-- Schedule Date Picker -->
              <v-menu
                bottom
                min-width="auto"
                offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                  :rules="[v => !!v || 'Date Required']"
                  :value="dateText(syncScheduleDate)"
                  :disabled="!syncTPRDate"
                  background-color="input"
                  class="mb-4"
                  clearable
                  readonly
                  dense
                  label="Day to Schedule Sync"
                  outlined
                  v-on="on"
                  v-bind="attrs"
                  @click:clear="syncScheduleDate = null">
                    <template #append-outer>
                      <v-tooltip right color="black">
                        <template #activator="{ on: tooltip, attrs: tooltipAttrs }">
                          <v-icon
                            v-bind="tooltipAttrs"
                            v-on="tooltip"
                            @click.stop>
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>Resyncing cannot be scheduled for Mondays or Tuesdays</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  :allowed-dates="allowedScheduleDates"
                  :show-current="true"
                  color="primary"
                  no-title
                  v-model="syncScheduleDate">
                </v-date-picker>
              </v-menu>
            </v-form>

            <v-btn
              block
              large
              color="primary"
              class="text-none font-weight-bold"
              elevation="2"
              @click="scheduleResync"
              :disabled="isMissingFields"
              :loading="loading"
            >
              <v-icon left>mdi-calendar</v-icon>
              Schedule Resync
            </v-btn>

            <v-divider class="my-6"></v-divider>

            <div class="text-caption text--secondary">
              <v-icon small color="grey">mdi-information-outline</v-icon>
              Scheduled Batch Resyncs will begin on the scheduled day at 6pm Central, and will resync all TPR Batches with the given TPR Date and batch type.
            </div>
          </v-card>
        </v-col>

        <!-- RIGHT SIDE: SYNC TABLE -->
        <v-col cols="12" md="8" lg="9" class="d-flex">
          <v-card outlined class="flex-card d-flex flex-column" style="width: 100%">
            <v-card-title class="d-flex align-center pb-2">
              <span class="text-h6 font-weight-bold">Scheduled Resyncs</span>
              <v-spacer></v-spacer>
            </v-card-title>
            <v-divider></v-divider>

            <v-data-table
              :headers="headers"
              :items="scheduledResyncs"
              :loading="loading"
              :height="tableSize"
              :items-per-page="pageSize"
              style="width: 100%"
              class="elevation-0 resync-table"
              fixed-header
              dense
              no-data-text="No Scheduled Resyncs"
              hide-default-footer
            >

              <template #[`item.scheduled_run_date`]="{ item }">
                <td class="noWrap text-center">
                  {{ item.scheduled_run_date ? formatDateDisplay(item.scheduled_run_date) : '---' }}
                </td>
              </template>

              <template #[`item.tpr_date`]="{ item }">
                <td class="noWrap text-center">
                  {{ item.tpr_date ? formatDateDisplay(item.tpr_date) : '---' }}
                </td>
              </template>

              <template #[`item.updated_on`]="{ item }">
                <td class="noWrap text-center">
                  {{ item.updated_on ? formatDateDisplay(item.updated_on) : '---' }}
                </td>
              </template>

              <template #[`item.batch_type`]="{ item }">
                <v-chip small
                v-if="item.batch_type" dark :color="getColor(item.batch_type)">
                  <span
                    style="
                      display: inline-block;
                      margin: 0 auto;
                      width: 120px;
                      text-align: center;
                    ">
                    <span>{{ item.batch_type }}</span>
                  </span>
                </v-chip>
              </template>

              <template #[`item.status`]="{ item }">
                <v-chip small
                v-if="item.status" dark :color="getColor(item.status)">
                  <span
                    style="
                      display: inline-block;
                      margin: 0 auto;
                      width: 120px;
                      text-align: center;
                    ">
                    <span>{{ item.status }}</span>
                  </span>
                </v-chip>
              </template>

              <template #[`item.delete_resync`]="{ item }">
                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-btn
                      :disabled="item.status != 'PENDING'"
                      v-on="on"
                      icon
                      @click="deleteResync(item)">
                      <v-icon color="red">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Scheduled Resync</span>
                </v-tooltip>
              </template>

              <template v-slot:footer>
                <v-divider></v-divider>
                <v-toolbar flat color="#fff" style="margin-top: 8px">
                  <v-row align="center" style="padding-bottom: 3px">
                    <v-spacer />
                    <v-col cols="2" style="max-width: 150px">
                      <v-select
                        :items="pageSizes"
                        :menu-props="{
                          top: true,
                          offsetY: true,
                          maxHeight: 500
                        }"
                        class="mt-7"
                        dense
                        label="Items Per Page"
                        v-model="pageSize">
                      </v-select>
                    </v-col>
                    <v-btn
                      :disabled="disablePrevious || loading"
                      @click="previous"
                      class="mx-2"
                      icon
                      large>
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <small>Page {{ page }}</small>
                    <v-btn
                      :disabled="disableNext || loading"
                      @click="next"
                      class="mx-2"
                      icon
                      large>
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </v-row>
                </v-toolbar>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { fullwidth } from "@/mixins/fullwidth"
import { paging } from "@/mixins/paging"
import { displayHelpers } from "@/mixins/displayHelpers"
import { userAccess } from "@/mixins/user-access"
import { utils } from "@/mixins/utils"
import { shared } from "@/mixins/shared"
import Resync from '@/axios/resync';

import moment from 'moment-timezone';

export default {
  name: "AdminBatchResync",
  mixins: [fullwidth, displayHelpers, paging, userAccess, utils, shared],
  data() {
    return {
      batchTypes: ['PERISHABLE', 'NON PERISHABLE'],
      loading: false,
      pageSizes: [10, 25, 50],
      pageSize: 25,
      nonTableHeight: 300,
      scheduledResyncs: [],
      syncBatchType: null,
      syncTPRDate: null,
      syncScheduleDate: null,
      headers: [
        { text: "Scheduled Resync Date", value: "scheduled_run_date", sortable: true, class: 'accent white--text', align: "center" },
        { text: "TPR Date", value: "tpr_date", sortable: true, class: 'accent white--text', align: "center" },
        { text: "Batch Type", value: "batch_type", sortable: false, class: 'accent white--text', align: "center" },
        { text: "Status", value: "status", sortable: false, class: 'accent white--text', align: "center" },
        { text: "Updated On", value: "updated_on", sortable: false, class: 'accent white--text', align: "center" },
        { text: "Delete Resync", value: "delete_resync", sortable: false, class: 'accent white--text', align: "center" }
      ]
    }
  },
  created() {
    this.getScheduledResyncs()
    this.tableChanged = this.tableChange(this.getScheduledResyncs)
  },
  computed: {
    isMissingFields() {
      return this.syncBatchType == null || this.syncTPRDate == null || this.syncScheduleDate == null
    },
    disablePrevious() {
      return (this.page - 1) * this.pageSize === 0
    },
    disableNext() {
      return this.scheduledResyncs.length < this.pageSize
    }
  },
  watch: {
    syncTPRDate() {
      this.syncScheduleDate = null
    }
  },
  methods: {
    async getScheduledResyncs() {
      const payload = {}

      try {
        this.loading = true
        const { data } = await Resync.getScheduledResyncs(payload, this.pageSize, this.from)
        this.scheduledResyncs = data
      } catch {
        console.log("WE GOT AN ERROR")

      } finally {
        this.loading = false
      }
    },
    allowedTPRDates(value) {
      const currDate = new Date()
      const checkDate = moment.tz(value, 'YYYY-MM-DD', 'America/Chicago').set({hour: 18, minute: 0, millisecond: 0 }).toDate()

      // Remove any day that isn't Sunday
      if (checkDate.getDay() != 0) return ''

      // Cannot resync TPR after 6 PM Central on the cut off date (14 days prior to the TPR Date)
      const cutOffDate = this.getCutOffDate(value)

      if (cutOffDate < currDate) return ''

      // Nothing caused an early return, so return the value
      return value
    },
    allowedScheduleDates(value) {
      const currDate = new Date()
      const checkDate = moment.tz(value, 'YYYY-MM-DD', 'America/Chicago').set({hour: 18, minute: 0, millisecond: 0 }).toDate()
      const cutOffDate = this.getCutOffDate(this.syncTPRDate)

      // Remove any Mondays or Tuesdays
      if (checkDate.getDay() == 1 || checkDate.getDay() == 2) return ''

      // Can't schedule a resync before the current date
      if (checkDate < currDate) return ''

      // Can't schedule a resync for after the cutoff date
      if (cutOffDate < checkDate) return ''

      // Can't schedule a resync for a date that is already scheduled
      if (this.scheduledResyncs.some((row) => row.scheduled_run_date === value && row.status !== 'CANCELED'))
        return ''

      // No early returns, return the valid date
      return value
    },
    getCutOffDate(dateString) {
      // Cannot resync TPR after 6 PM Central on the cut off date (14 days prior to the TPR Date)
      const cutOffDate = moment.tz(dateString, 'YYYY-MM-DD', 'America/Chicago')
        .subtract(14, 'days')
        .set({ hour: 18, minute: 0, second: 0, millisecond: 0 })
        .toDate()
      return cutOffDate
    },
    dateText(date) {
      return date
        ? this.moment(date).format(this.$dateConfig.date_display)
        : ''
    },
    async scheduleResync() {
      const valid = this.$refs['sync-form'].validate()
      if (valid) {
        try {
          this.loading = true
          const payload = {
            "scheduled_run_date": this.syncScheduleDate,
            "tpr_date": this.syncTPRDate,
            "batch_type": this.syncBatchType
          }

          const response = await Resync.scheduleFullResync(payload)
          console.log(response)

          if (response.status == 200) {
            const total = response.data.total
            console.log(total)
            this.$store.dispatch('setSnackbar', {
              status: 'success', 
              text: `Scheduled ${total} batches to be resynced on ${this.formatDateDisplay(this.syncScheduleDate)}`
            })
          }
        }
        catch (error) {
          console.log(error.response)
          this.$store.dispatch('setSnackbar', {
            status: 'error', 
            text: error.response.data.message
          })
        } finally {
          this.loading = false
          this.getScheduledResyncs()
        }
      }
    },
    async deleteResync(item) {
      this.loading = true

      try {
        const response = await Resync.deleteScheduledResync(item.resync_id)
        
        if (response.status == 200) {
          this.$store.dispatch('setSnackbar', {
            status: 'success',
            text: `Deleted Resync Scheduled for ${this.formatDateDisplay(item.scheduled_run_date)}`,
          })
        }
      } catch {

      } finally {
        this.loading = false
        this.getScheduledResyncs()
      }
    }
  }
}
</script>
<style scoped>

.resync-table ::v-deep th.sortable.text-center::before {
  content: "";
  display: inline-block;
  width: 18px;
}

</style>